import { Link } from "react-router-dom";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { blogPosts } from "../../mocks/blogPosts";
import { blogPostsEn } from "../../mocks/blogPosts.en";
import InternalNavbar from "../../components/feature/InternalNavbar";

export default function BlogPage() {
  const { i18n } = useTranslation();
  const isEn = i18n.language === "en";
  const allPosts = isEn ? blogPostsEn : blogPosts;
  const featured = allPosts[0];
  const rest = allPosts.slice(1);
  const [email, setEmail] = useState("");
  const [formStatus, setFormStatus] = useState<"idle" | "loading" | "success" | "error">("idle");

  const navLinks = [
    { label: isEn ? "About us" : "Sobre nosotros", href: "/sobre-nosotros" },
    { label: "Roadmap", href: "/roadmap" },
  ];

  const handleNewsletter = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!email) return;
    setFormStatus("loading");
    try {
      const body = new URLSearchParams({ email });
      const res = await fetch("https://readdy.ai/api/form/d6v7l1edp0k8rarn7sv0", { method: "POST", headers: { "Content-Type": "application/x-www-form-urlencoded" }, body: body.toString() });
      if (res.ok) { setFormStatus("success"); setEmail(""); } else { setFormStatus("error"); }
    } catch { setFormStatus("error"); }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col" style={{ fontFamily: "'Inter', sans-serif" }}>
      <InternalNavbar links={navLinks} rightCta={{ label: isEn ? "See plans" : "Ver planes", href: "/#precios" }} />
      <main className="flex-1">
        <section className="py-16 px-6 border-b border-stone-100">
          <div className="max-w-5xl mx-auto">
            <span className="inline-block text-xs font-bold tracking-widest uppercase text-amber-600 mb-4">Blog</span>
            <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6">
              <h1 className="text-4xl font-bold text-stone-900" style={{ fontFamily: "'Playfair Display', serif" }}>
                {isEn ? "News, guides &\nuse cases" : "Noticias, guías y\ncasos de uso"}
              </h1>
              <p className="text-stone-500 text-sm max-w-xs">
                {isEn ? "Everything about voice AI, automatic reception and growing your business." : "Todo sobre IA de voz, recepción automática y cómo hacer crecer tu negocio."}
              </p>
            </div>
          </div>
        </section>
        <section className="py-14 px-6">
          <div className="max-w-5xl mx-auto space-y-12">
            {featured && (
              <article className="grid lg:grid-cols-2 gap-8 items-center">
                <Link to={`/blog/${featured.slug}`} className="block aspect-video rounded-2xl overflow-hidden cursor-pointer">
                  <img src={featured.img} alt={featured.title} className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-500" />
                </Link>
                <div>
                  <div className="flex items-center gap-2 mb-4">
                    <span className="px-2.5 py-0.5 bg-amber-100 text-amber-700 text-xs font-bold rounded-full">{featured.category}</span>
                    <span className="text-stone-400 text-xs">{isEn ? "Featured" : "Destacado"}</span>
                  </div>
                  <Link to={`/blog/${featured.slug}`} className="block cursor-pointer">
                    <h2 className="text-2xl font-bold text-stone-900 mb-3 leading-tight hover:text-amber-600 transition-colors" style={{ fontFamily: "'Playfair Display', serif" }}>{featured.title}</h2>
                  </Link>
                  <p className="text-stone-500 text-sm leading-relaxed mb-5">{featured.excerpt}</p>
                  <div className="flex items-center gap-4 text-xs text-stone-400 mb-5">
                    <span className="flex items-center gap-1"><i className="ri-calendar-line" />{featured.date}</span>
                    <span className="flex items-center gap-1"><i className="ri-time-line" />{featured.readTime} {isEn ? "read" : "lectura"}</span>
                  </div>
                  <Link to={`/blog/${featured.slug}`} className="inline-block px-5 py-2.5 bg-stone-900 hover:bg-stone-700 text-white text-sm font-semibold rounded-xl transition-colors cursor-pointer whitespace-nowrap">
                    {isEn ? "Read article" : "Leer artículo"}
                  </Link>
                </div>
              </article>
            )}
            {rest.length > 0 && (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 pt-4 border-t border-stone-100">
                {rest.map((post) => (
                  <Link key={post.slug} to={`/blog/${post.slug}`} className="group block cursor-pointer">
                    <div className="aspect-video rounded-xl overflow-hidden mb-4">
                      <img src={post.img} alt={post.title} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" />
                    </div>
                    <div className="flex items-center gap-2 mb-2"><span className="px-2 py-0.5 bg-stone-100 text-stone-600 text-xs font-bold rounded-full">{post.category}</span></div>
                    <h3 className="font-bold text-stone-900 text-sm leading-snug mb-2 group-hover:text-amber-600 transition-colors">{post.title}</h3>
                    <p className="text-stone-500 text-xs leading-relaxed mb-3">{post.excerpt}</p>
                    <div className="flex items-center gap-3 text-xs text-stone-400"><span>{post.date}</span><span>·</span><span>{post.readTime}</span></div>
                  </Link>
                ))}
              </div>
            )}
            {allPosts.length === 0 && <div className="text-center py-16 text-stone-400 text-sm">{isEn ? "No articles available yet." : "No hay artículos disponibles todavía."}</div>}
          </div>
        </section>
        <section className="py-20 px-6 border-t border-stone-100" style={{ background: "#FAFAF9" }}>
          <div className="max-w-xl mx-auto text-center">
            <span className="inline-block text-xs font-bold tracking-widest uppercase text-amber-600 mb-4">Newsletter</span>
            <h2 className="text-2xl font-bold text-stone-900 mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>
              {isEn ? "Get the best articles" : "Recibe los mejores artículos"}
            </h2>
            <p className="text-stone-500 text-sm mb-6">{isEn ? "Every two weeks, the best on voice AI and automation for businesses." : "Cada dos semanas, lo mejor sobre IA de voz y automatización para negocios."}</p>
            {formStatus === "success" ? (
              <div className="flex items-center justify-center gap-2 py-4 px-6 bg-green-50 border border-green-200 rounded-xl max-w-sm mx-auto">
                <i className="ri-checkbox-circle-line text-green-500 text-lg" />
                <p className="text-green-700 text-sm font-medium">{isEn ? "Subscribed! We'll write to you soon." : "¡Suscrito! Te escribiremos pronto."}</p>
              </div>
            ) : (
              <form data-readdy-form className="flex gap-2 max-w-sm mx-auto" onSubmit={handleNewsletter}>
                <input type="email" name="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder={isEn ? "your@email.com" : "tu@email.com"} className="flex-1 border border-stone-200 text-stone-900 text-sm rounded-xl px-4 py-2.5 outline-none focus:border-amber-500 transition-colors" required disabled={formStatus === "loading"} />
                <button type="submit" disabled={formStatus === "loading"} className="px-4 py-2.5 bg-amber-500 hover:bg-amber-400 disabled:opacity-60 text-white text-sm font-semibold rounded-xl transition-colors cursor-pointer whitespace-nowrap">
                  {formStatus === "loading" ? <span className="flex items-center gap-1.5"><i className="ri-loader-4-line animate-spin" />{isEn ? "Sending" : "Enviando"}</span> : isEn ? "Subscribe" : "Suscribirme"}
                </button>
              </form>
            )}
            {formStatus === "error" && <p className="text-red-500 text-xs mt-3">{isEn ? "Something went wrong. Try again." : "Algo salió mal. Inténtalo de nuevo."}</p>}
          </div>
        </section>
      </main>
      <footer className="bg-stone-50 border-t border-stone-100 px-6 py-6">
        <div className="max-w-5xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-stone-400 text-xs">{isEn ? "© 2026 Auria. All rights reserved." : "© 2026 Auria. Todos los derechos reservados."}</p>
          <div className="flex items-center gap-4 text-xs">
            <Link to="/" className="text-stone-400 hover:text-stone-600 transition-colors cursor-pointer">{isEn ? "Home" : "Inicio"}</Link>
            <Link to="/sobre-nosotros" className="text-stone-400 hover:text-stone-600 transition-colors cursor-pointer">{isEn ? "About" : "Sobre nosotros"}</Link>
            <Link to="/roadmap" className="text-stone-400 hover:text-stone-600 transition-colors cursor-pointer">Roadmap</Link>
            <Link to="/privacidad" className="text-stone-400 hover:text-stone-600 transition-colors cursor-pointer">{isEn ? "Privacy" : "Privacidad"}</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
